import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Users, Award, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PROPERTY_IMAGES } from '../utils/constants';
import ScrollReveal from './ScrollReveal';

const EventsSection = () => {
  const navigate = useNavigate();
  
  const eventTypes = [
    {
      icon: Heart,
      title: "Dream Weddings",
      description: "Create magical moments at Sarovar Garden - our stunning lakeside floating venue",
      image: PROPERTY_IMAGES.jalsaRetreat.sarovarGarden,
      features: ["Sarovar Garden (Floating Venue)", "Grand Banquet", "Catering", "Décor"]
    },
    {
      icon: Users,
      title: "Corporate Events",
      description: "State-of-the-art facilities for conferences, seminars, and business meetings",
      image: PROPERTY_IMAGES.jalsaRetreat.banquet,
      features: ["Meeting Rooms", "AV Equipment", "Catering", "Team Building"]
    },
    {
      icon: Calendar,
      title: "Social Gatherings",
      description: "Perfect venues for birthdays, anniversaries, and family celebrations",
      image: PROPERTY_IMAGES.jalsaRetreat.reception,
      features: ["Flexible Spaces", "Custom Menus", "Entertainment", "Photography"]
    }
  ];

  return (
    <section id="events" className="relative py-24 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4AF37' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <ScrollReveal direction="up">
          <div className="text-center mb-20">
            <div className="flex items-center justify-center mb-6">
              <div className="h-px w-20 bg-gradient-to-r from-transparent to-gold-400" />
              <Award className="mx-4 w-6 h-6 text-gold-400" />
              <div className="h-px w-20 bg-gradient-to-l from-transparent to-gold-400" />
            </div>
            
            <h2 className="text-5xl md:text-6xl font-serif text-navy-900 mb-6">
              Events & Celebrations
            </h2>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
              From intimate gatherings to grand celebrations, we bring your vision to life 
              with impeccable service and stunning venues.
            </p>
          </div>
        </ScrollReveal>

        {/* Event Types Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {eventTypes.map((event, index) => (
            <ScrollReveal key={index} direction="up" delay={index * 0.15}>
              <motion.div 
                whileHover={{ y: -10 }}
                className="group relative bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500"
              >
                {/* Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={event.image}
                    alt={event.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-900/90 to-transparent" />
                  
                  {/* Icon */}
                  <div className="absolute top-6 left-6 w-14 h-14 bg-gold-400 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <event.icon className="w-7 h-7 text-navy-900" />
                  </div>
                </div>

                {/* Content */}
                <div className="p-8">
                  <h3 className="text-2xl font-serif text-navy-900 mb-3">
                    {event.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {event.description}
                  </p>

                  {/* Features */}
                  <div className="space-y-2 mb-6">
                    {event.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-2 text-sm text-gray-700">
                        <div className="w-1.5 h-1.5 bg-gold-400 rotate-45" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <motion.button 
                    whileHover={{ x: 5 }}
                    className="text-gold-500 font-semibold hover:text-gold-600 transition-colors duration-300 flex items-center space-x-2"
                  >
                    <span>Learn More</span>
                    <span>→</span>
                  </motion.button>
                </div>
              </motion.div>
            </ScrollReveal>
          ))}
        </div>

        {/* CTA Banner */}
        <ScrollReveal direction="up" delay={0.3}>
          <div className="relative bg-gradient-to-r from-navy-900 to-navy-800 rounded-2xl p-12 text-center overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23D4AF37' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`
              }} />
            </div>
            
            <div className="relative z-10">
              <h3 className="text-3xl md:text-4xl font-serif text-white mb-4">
                Ready to Plan Your Event?
              </h3>
              <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
                Let our event specialists help you create an unforgettable experience
              </p>
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/properties/jalsa-retreat?book=true')}
                className="px-10 py-4 bg-gold-400 text-navy-900 hover:bg-white transition-all duration-300 font-semibold tracking-wider rounded shadow-xl hover:shadow-2xl"
              >
                REQUEST PROPOSAL
              </motion.button>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default EventsSection;
